import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useSession } from '@supabase/auth-helpers-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { calculateCurrentAge, formatAge, validateDateOfBirth } from '../lib/ageCalculation';

interface AddChildModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (name: string, dateOfBirth: string) => Promise<void>;
}

export default function AddChildModal({ isOpen, onClose, onAdd }: AddChildModalProps) {
  const [name, setName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !dateOfBirth) {
      setValidationError('Name and date of birth are required');
      return;
    }

    // Validate date of birth
    const validation = validateDateOfBirth(dateOfBirth);
    if (!validation.isValid) {
      setValidationError(validation.error || 'Invalid date of birth');
      return;
    }

    setValidationError(null);
    setIsSubmitting(true);
    try {
      await onAdd(name, dateOfBirth);
      setName('');
      setDateOfBirth('');
      setValidationError(null);
      onClose();
    } catch (error) {
      console.error('Error in AddChildModal:', error);
      setValidationError('Failed to add child. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when modal closes
  const handleClose = () => {
    setName('');
    setDateOfBirth('');
    setValidationError(null);
    onClose();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <form onSubmit={handleSubmit}>
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="absolute right-0 top-0 pr-4 pt-4">
                      <button
                        type="button"
                        className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                        onClick={onClose}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold leading-6 text-gray-900">
                          Add Child
                        </h3>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                            Child's Name
                          </label>
                          <input
                            type="text"
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            required
                            minLength={2}
                            maxLength={50}
                          />
                        </div>

                        <div>
                          <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700">
                            Date of Birth
                          </label>
                          <input
                            type="date"
                            id="date_of_birth"
                            value={dateOfBirth}
                            onChange={(e) => {
                              setDateOfBirth(e.target.value);
                              setValidationError(null); // Clear error on change
                            }}
                            className={`mt-1 block w-full rounded-md border ${
                              validationError ? 'border-red-300' : 'border-gray-300'
                            } px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary date-picker-primary`}
                            required
                            max={new Date().toISOString().split('T')[0]} // Cannot be in the future
                          />
                          {dateOfBirth && (
                            <p className="mt-1 text-xs text-gray-500">
                              Age: {formatAge(calculateCurrentAge(dateOfBirth))}
                            </p>
                          )}
                          {validationError && (
                            <p className="mt-1 text-xs text-red-600">{validationError}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn btn-primary sm:ml-3 sm:w-auto"
                    >
                      {isSubmitting ? 'Adding...' : 'Add Child'}
                    </button>
                      <button
                        type="button"
                        className="btn btn-secondary mt-3 sm:mt-0 sm:w-auto"
                        onClick={handleClose}
                      >
                        Cancel
                      </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}